import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import client from "../../../api/client";
import { GlobalContext } from "../../../context";
import { logoutHandler } from "../../../context/actions/auth";
import { goToCommunity } from "../../../utils/community";
import Logo from "../../AllVon/VonHeader/assets/logo.png";
import { setZone, useZone } from "../home/time";
import Icon from "./Icon";
import { useAiAccess } from "./ai/aiApi";
import NotificationBell from "../NotificationBell";
import MobileNav, { useMobile } from "./MobileNav";
import "./lms.css";

/** The student's courses summary (/me/overview), loaded once by the shell and shared with Home. */
export const LmsContext = createContext({ overview: null, overviewError: false, reloadOverview: () => {} });
export const useLms = () => useContext(LmsContext);

const NAV = [
  ["Learn", [
    ["Home", "/dashboard", "home", (p) => p === "/dashboard"],
    ["My Courses", "/dashboard/my-courses", "book", (p) => p.startsWith("/dashboard/my-courses") || p.startsWith("/dashboard/course/")],
    ["Grades", "/dashboard/grades", "chart"],
    ["Projects", "/dashboard/projects", "folder"],
  ]],
  ["Career", [
    ["Certificates", "/dashboard/certificates", "award"],
    ["Job Board", "/dashboard/job-board", "brief"],
    ["CV Reviewer", "/dashboard/cv-review", "doc"],
    ["AI Career Advisor", "/dashboard/career-advisor", "chat"],
  ]],
  ["Account", [
    ["Settings", "/dashboard/settings", "gear"],
  ]],
];

/**
 * Student dashboard frame from the approved prototype: light sidebar (logo, Learn / Career /
 * Account, cohort room, the student) and the page beside it. Staff keep the old layout.
 */
export default function LmsShell({ children }) {
  const { pathname } = useLocation();
  const mobile = useMobile();
  const [menu, setMenu] = useState(false);
  useEffect(() => setMenu(false), [pathname]);
  const navigate = useNavigate();
  const { authState: { data }, authDispatch } = useContext(GlobalContext);
  const user = data?.user;
  const [overview, setOverview] = useState(null);
  const [overviewError, setOverviewError] = useState(false);
  const [joining, setJoining] = useState(false);
  const [confirmOut, setConfirmOut] = useState(false);
  const zone = useZone();
  const ai = useAiAccess();
  const nav = NAV.map(([group, items]) => [group, items.filter(([, to]) => ai || to !== "/dashboard/career-advisor")]);
  // Times follow the zone saved on the account (Settings › Profile); WAT when none is set.
  useEffect(() => { if (user) setZone(user.time_zone); }, [user]);

  const reloadOverview = useCallback(() => {
    client.get("/me/overview").then((r) => { setOverview(r.data); setOverviewError(false); }).catch(() => setOverviewError(true));
  }, []);
  useEffect(() => { reloadOverview(); }, [reloadOverview]);

  const active = overview?.courses?.find((c) => c.status === "active");
  const subtitle = overview ? (active ? active.cohort.name : "Alumni") : "";
  const initials = `${user?.firstname?.[0] ?? ""}${user?.lastname?.[0] ?? ""}`.toUpperCase() || "E1";

  const joinCommunity = async () => {
    if (joining) return;
    setJoining(true);
    try {
      await goToCommunity();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Couldn't open the cohort room. Please try again.");
      setJoining(false);
    }
  };

  const logOut = async () => {
    await logoutHandler(authDispatch);
    navigate("/");
  };

  return (
    <LmsContext.Provider value={{ overview, overviewError, reloadOverview }}>
      <div className="lms-frame student">
        <div className="lms lms-side-wrap">
          <MobileNav open={menu} setOpen={setMenu} pathname={pathname} tabs={nav[0][1]} />
          <aside className={`side ${menu ? "open" : ""}`}>
            <div className="staff-top">
              <Link to="/" className="logo"><img src={Logo} alt="E1X Academy" /></Link>
              {!mobile && <NotificationBell />}
            </div>
            {nav.map(([group, items]) => (
              <div key={group}>
                <div className="nav-group">{group}</div>
                <nav className="nav">
                  {items.map(([label, to, icon, match]) => (
                    <Link key={to} to={to} className={(match ? match(pathname) : pathname.startsWith(to)) ? "on" : ""}>
                      <Icon name={icon} />{label}
                    </Link>
                  ))}
                </nav>
              </div>
            ))}
            {(!overview || active) && (
              <button type="button" className="community" onClick={joinCommunity} disabled={joining}>
                <Icon name="chat" size={20} />
                <div><strong>Cohort Room</strong><span>{joining ? "Opening…" : "Chat with your cohort"}</span></div>
              </button>
            )}
            {confirmOut ? (
              <div className="logout-confirm">
                <span>Log out?</span>
                <button className="cbtn quiet" type="button" onClick={() => setConfirmOut(false)}>Cancel</button>
                <button className="cbtn danger" type="button" onClick={logOut}>Log Out</button>
              </div>
            ) : (
              <div className="me">
                <span className="av">{user?.profile_img ? <img src={user.profile_img} alt="" /> : initials}</span>
                <div><strong>{user?.firstname} {user?.lastname ? `${user.lastname[0]}.` : ""}</strong><small title={subtitle}>{subtitle}</small></div>
                <button type="button" className="out" title="Log Out" aria-label="Log Out" onClick={() => setConfirmOut(true)}><Icon name="out" size={16} /></button>
              </div>
            )}
          </aside>
        </div>
        {/* Redraw the page when the student changes their time zone. */}
        <main key={zone} className="has-tabs">{children}</main>
      </div>
    </LmsContext.Provider>
  );
}
